import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {useSelector} from "react-redux";
import {
    Box, Button, Card, CardContent, Chip,
    Dialog, DialogActions, DialogContent, Divider, FormControl,
    Grid, InputLabel, Link as MuiLink, MenuItem, Paper, Select, Stack, Tab, Tabs, Table, TableBody,
    TableCell, TableHead, TableRow,
    TextField, Typography, CircularProgress
} from "@mui/material"
import {TabContext, TabPanel} from "@mui/lab";
import {LoadingButton} from "@mui/lab";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import StarIcon from "@mui/icons-material/Star";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SearchIcon from "@mui/icons-material/Search";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import {SnackBarContext} from "../../snackBarProvider";
import Loading from "../../components/Common/Loading";
import {format} from "date-fns";
import {
    useAddPlayerToClanTOHMutation,
    useRemovePlayerFromClanTOHMutation,
    useAddClanExperienceTOHMutation,
    useAddPlayerReputationTOHMutation,
    useDeleteClanTOHMutation,
    useSetClanLeaderTOHMutation,
    useRenameClanTOHMutation,
    useGetUserClanInfoTOHQuery,
    useSearchClanByNameTOHMutation,
    useGetGlobalChatTOHMutation,
    useGetClanChatTOHMutation,
    useMutePlayerTOHMutation,
    useUnmutePlayerTOHMutation,
} from "../../core/api/toh-clans-api";
import TohBanStatusCard from "../BanControl/TohBanStatusCard";
import BossEventsTOH from "../BossEvents/toh";
import DepotEventsTOH from "../DepotEvents/toh";
import ClashEventsTOH from "../ClashEvents/toh";
import ResearchesTOH from "../Researches/toh";
import MembershipHistoryTOH from "../MembershipHistory/toh";
import { useIsCheaterV1TohQuery } from "../../core/api/toh-social-api"


function ClansTOH() {
    const {action, tab: tabParam, game} = useParams()
    const CommonData = useSelector((state) => state.data.CommonData)
    const deviceId = CommonData.deviceId

    const deviceIdIsEmpty = deviceId == null || deviceId == ''  || deviceId == '-' || typeof deviceId == 'object';
    const link_prefix = deviceIdIsEmpty
        ? '/' + game + '/' + action + '/-/'
        : '/' + game + '/' + action + '/' + deviceId + '/'

    const tab = tabParam || 'clan'
    const [tabIndex, setTabIndex] = useState(tab);

    const handleTabChange = (event, newTabIndex) => {
        setTabIndex(newTabIndex);
    };

    // Shared clan id across tabs: starts from player's clan, can be edited from
    // Clan tab (Search → Use this Clan ID) and reused in Boss Events tab.
    const {data: clanInfo} = useGetUserClanInfoTOHQuery(deviceId, {skip: deviceIdIsEmpty});
    const playerClanId = clanInfo?.clan?.id || '';
    const [selectedClanId, setSelectedClanId] = useState('');
    const [selectedClanIdTouched, setSelectedClanIdTouched] = useState(false);

    useEffect(() => {
        if (!selectedClanIdTouched) setSelectedClanId(playerClanId);
    }, [playerClanId, selectedClanIdTouched]);

    const updateSelectedClanId = (value) => {
        setSelectedClanId(value);
        setSelectedClanIdTouched(true);
    };

    return (
        <TabContext value={tabIndex}>
            <Stack>
                <Card>
                    <Tabs value={tabIndex} onChange={handleTabChange}>
                        <Tab label="Clan" value="clan" component={Link} to={link_prefix + "clan"}/>
                        <Tab label="Chat" value="chat" component={Link} to={link_prefix + "chat"}/>
                        <Tab label="Clan Chat" value="clan-chat" component={Link} to={link_prefix + "clan-chat"}/>
                        <Tab label="Boss Events" value="boss-events" component={Link} to={link_prefix + "boss-events"}/>
                        <Tab label="Depot Events" value="depot" component={Link} to={link_prefix + "depot"}/>
                        <Tab label="Clash Events" value="clash" component={Link} to={link_prefix + "clash"}/>
                        <Tab label="Researches" value="researches" component={Link} to={link_prefix + "researches"}/>
                        <Tab label="Membership History" value="membership-history" component={Link} to={link_prefix + "membership-history"}/>
                    </Tabs>
                </Card>

                <TabPanel value="clan">
                    <ManagementTab
                        deviceId={deviceId}
                        deviceIdIsEmpty={deviceIdIsEmpty}
                        clanInfo={clanInfo}
                        clanId={selectedClanId}
                        setClanId={updateSelectedClanId}
                    />
                </TabPanel>

                <TabPanel value="chat">
                    <ChatTab/>
                </TabPanel>

                <TabPanel value="clan-chat">
                    <ClanChatTab defaultClanId={selectedClanId}/>
                </TabPanel>

                <TabPanel value="boss-events">
                    <BossEventsTOH
                        deviceId={deviceIdIsEmpty ? '' : deviceId}
                        defaultClanId={selectedClanId}
                    />
                </TabPanel>

                <TabPanel value="depot">
                    <DepotEventsTOH
                        deviceId={deviceIdIsEmpty ? '' : deviceId}
                        defaultClanId={selectedClanId}
                    />
                </TabPanel>

                <TabPanel value="clash">
                    <ClashEventsTOH defaultClanId={selectedClanId}/>
                </TabPanel>

                <TabPanel value="researches">
                    <ResearchesTOH defaultClanId={selectedClanId} deviceId={deviceIdIsEmpty ? '' : deviceId}/>
                </TabPanel>

                <TabPanel value="membership-history">
                    <MembershipHistoryTOH defaultClanId={selectedClanId}/>
                </TabPanel>
            </Stack>
        </TabContext>
    );
}


const formatTimestamp = (ts) => {
    if (!ts) return '-';
    try { return format(ts * 1000, "dd.MM.yyyy HH:mm:ss"); } catch { return String(ts); }
};


function ClanInfoTable({clan}) {
    if (!clan) return null;
    return (
        <Table size="small">
            <TableBody>
                <TableRow>
                    <TableCell><strong>Clan ID</strong></TableCell>
                    <TableCell>{clan.id}</TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell>{clan.name}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell><strong>Leader</strong></TableCell>
                    <TableCell>{clan.leader}</TableCell>
                    <TableCell><strong>Members</strong></TableCell>
                    <TableCell>{clan.memberCount}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell><strong>Level</strong></TableCell>
                    <TableCell>{clan.levelInfo?.level}</TableCell>
                    <TableCell><strong>Experience</strong></TableCell>
                    <TableCell>{clan.levelInfo?.currentExperienceProgress} / {clan.levelInfo?.nextLevelExperience}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell><strong>Power Sum</strong></TableCell>
                    <TableCell>{clan.statistics?.powerSum}</TableCell>
                    <TableCell><strong>Private</strong></TableCell>
                    <TableCell>{clan.isPrivate ? 'Yes' : 'No'}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell><strong>Created</strong></TableCell>
                    <TableCell>{formatTimestamp(clan.createdAt)}</TableCell>
                    <TableCell><strong>Updated</strong></TableCell>
                    <TableCell>{formatTimestamp(clan.updatedAt)}</TableCell>
                </TableRow>
            </TableBody>
        </Table>
    );
}


function MembersTable({members}) {
    const {game} = useParams();
    if (!members || members.length === 0) return null;
    const stateUrl = (id) => `/${game}/state/${id}`;
    return (
        <>
            <Typography sx={{mt: 2}} variant="h5">Members</Typography>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Level</TableCell>
                        <TableCell>Country</TableCell>
                        <TableCell>Activity</TableCell>
                        <TableCell>Joined</TableCell>
                        <TableCell>Banned</TableCell>
                        <TableCell>Muted</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {members.map((member) => (
                        <TableRow key={member.id}>
                            <TableCell sx={{maxWidth: 150}}>
                                <Stack direction="row" spacing={0.5} sx={{alignItems: 'center'}}>
                                    <MuiLink
                                        href={stateUrl(member.id)} target="_blank" rel="noopener" underline="hover"
                                        sx={{wordBreak: 'break-all', minWidth: 0}}>
                                        {member.id}
                                    </MuiLink>
                                </Stack>
                            </TableCell>
                            <TableCell>{member.name}</TableCell>
                            <TableCell>{member.role}</TableCell>
                            <TableCell>{member.level}</TableCell>
                            <TableCell>{member.country}</TableCell>
                            <TableCell>{member.activityScore}</TableCell>
                            <TableCell>{formatTimestamp(member.joinedAt)}</TableCell>
                            <BanStatusTableCell userId={member.id} />
                            <MuteStatusTableCell member={member} />
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </>
    );
}

const BanStatusTableCell = ({ userId }) => {
    const { data, isLoading, isFetching } = useIsCheaterV1TohQuery(
        userId,
        { skip: userId === "" }
    )

    return (
        <TableCell>
            {
                isLoading || isFetching
                ? <CircularProgress />
                : data?.cheater
                ? "Yes"
                : "No"
            }
        </TableCell>
    )
}

const MuteStatusTableCell = ({ member }) => {
    return (
        <TableCell>
            {
                member?.mutedUntil != null && new Date(member?.mutedUntil * 1000) > new Date()
                ? "Yes"
                : "No"
            }
        </TableCell>
    )
}


function ManagementTab({deviceId, deviceIdIsEmpty, clanInfo, clanId, setClanId}) {
    const useSnackBars = () => useContext(SnackBarContext);
    const {addAlert} = useSnackBars();

    const {
        isLoading: isClanInfoLoading,
        isError: isClanInfoError,
        error: clanInfoError,
    } = useGetUserClanInfoTOHQuery(deviceId, {skip: deviceIdIsEmpty});

    // Search by name
    const [searchClan, searchClanResponse] = useSearchClanByNameTOHMutation();
    const [searchName, setSearchName] = useState('');
    const searchResult = searchClanResponse.data;

    const handleSearch = async () => {
        const response = await searchClan(searchName);
        if (response.hasOwnProperty('error')) {
            addAlert({text: response.error?.data?.message || 'Clan not found', type: 'error'});
        }
    };

    // Actions
    const [addPlayer, addPlayerResponse] = useAddPlayerToClanTOHMutation();
    const [removePlayer, removePlayerResponse] = useRemovePlayerFromClanTOHMutation();
    const [deleteClan, deleteClanResponse] = useDeleteClanTOHMutation();
    const [setLeader, setLeaderResponse] = useSetClanLeaderTOHMutation();
    const [renameClan, renameClanResponse] = useRenameClanTOHMutation();
    const [addExperience, addExperienceResponse] = useAddClanExperienceTOHMutation();
    const [addReputation, addReputationResponse] = useAddPlayerReputationTOHMutation();

    const clanIdFromSearch = searchResult?.id || '';
    const applyClanIdFromSearch = () => {
        if (clanIdFromSearch) {
            setClanId(clanIdFromSearch);
        }
    };

    const [newName, setNewName] = useState('');
    const [experience, setExperience] = useState('');
    const [repScore, setRepScore] = useState('');

    const [confirmDialog, setConfirmDialog] = useState({open: false, title: '', message: '', onConfirm: null});

    const openConfirm = (title, message, onConfirm) => {
        setConfirmDialog({open: true, title, message, onConfirm});
    };

    const closeConfirm = () => {
        setConfirmDialog({open: false, title: '', message: '', onConfirm: null});
    };

    const handleAction = async (action, params, successMsg) => {
        const response = await action(params);
        if (response.hasOwnProperty('error')) {
            addAlert({text: response.error?.data?.message || 'Error', type: 'error'});
        } else {
            addAlert({text: successMsg, type: 'success'});
        }
        closeConfirm();
    };

    const clanIdEmpty = !clanId || clanId.trim() === '';

    return (
        <>
            <Grid container spacing={2}>
                {/* Player's Clan Info */}
                <Grid size={8}>

                    {!deviceIdIsEmpty && (
                        <Loading isLoading={isClanInfoLoading} isError={isClanInfoError} error={clanInfoError}>
                            {clanInfo?.clan ? (
                                <Card>
                                    <CardContent>
                                        <Typography variant="h5" gutterBottom>Player Clan</Typography>
                                        <ClanInfoTable clan={clanInfo.clan}/>
                                        <MembersTable members={clanInfo.clan.members}/>
                                    </CardContent>
                                </Card>
                            ) : (
                                <Card>
                                    <CardContent>
                                        <Typography sx={{
                                            color: "text.secondary"
                                        }}>Player is not in a clan</Typography>
                                    </CardContent>
                                </Card>
                            )}
                        </Loading>
                )}

                    <Card sx={{mb:2}}>
                        <CardContent>
                            <Typography variant="h5" gutterBottom>Search Clan by Name</Typography>
                            <Stack direction="row" spacing={1} sx={{
                                alignItems: "center"
                            }}>
                                <TextField
                                    label="Clan Name" size="small" value={searchName}
                                    onChange={(e) => setSearchName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && searchName.trim()) handleSearch(); }}
                                />
                                <LoadingButton
                                    loading={searchClanResponse.isLoading}
                                    variant="outlined" color="info"
                                    startIcon={<SearchIcon/>}
                                    disabled={!searchName.trim()}
                                    onClick={handleSearch}>
                                    Search
                                </LoadingButton>
                            </Stack>

                            {searchResult && (
                                <Box sx={{mt: 2}}>
                                    <ClanInfoTable clan={searchResult}/>
                                    <MembersTable members={searchResult.members}/>
                                    <Button
                                        size="small" sx={{mt: 1}}
                                        variant="outlined" color="info"
                                        onClick={applyClanIdFromSearch}>
                                        Use this Clan ID in actions
                                    </Button>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            {!deviceIdIsEmpty && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "text.secondary",
                                        mb: 1
                                    }}>
                                    Player: <strong>{deviceId}</strong>
                                </Typography>
                            )}
                            <TextField
                                fullWidth label="Clan ID (UUID)" size="small" sx={{mb: 2}}
                                value={clanId}
                                onChange={(e) => setClanId(e.target.value)}
                            />

                            <Stack direction="row" spacing={1}>
                                <LoadingButton
                                    loading={addPlayerResponse.isLoading}
                                    variant="outlined" color="success"
                                    startIcon={<PersonAddIcon/>}
                                    disabled={clanIdEmpty || deviceIdIsEmpty}
                                    onClick={() => openConfirm('Add Player to Clan',
                                        `Add player ${deviceId} to clan ${clanId}?`,
                                        () => handleAction(addPlayer, {userId: deviceId, clanId}, 'Player added to clan'))}>
                                    Add to Clan
                                </LoadingButton>

                                <LoadingButton
                                    loading={removePlayerResponse.isLoading}
                                    variant="outlined" color="error"
                                    startIcon={<PersonRemoveIcon/>}
                                    disabled={clanIdEmpty || deviceIdIsEmpty}
                                    onClick={() => openConfirm('Remove Player from Clan',
                                        `Remove player ${deviceId} from clan ${clanId}?`,
                                        () => handleAction(removePlayer, {userId: deviceId, clanId}, 'Player removed from clan'))}>
                                    Remove from Clan
                                </LoadingButton>

                                <LoadingButton
                                    loading={setLeaderResponse.isLoading}
                                    variant="outlined" color="info"
                                    startIcon={<StarIcon/>}
                                    disabled={deviceIdIsEmpty}
                                    onClick={() => openConfirm('Set Clan Leader',
                                        `Set player ${deviceId} as clan leader?`,
                                        () => handleAction(setLeader, {userId: deviceId}, 'Clan leader updated'))}>
                                    Set as Leader
                                </LoadingButton>

                                <LoadingButton
                                    loading={deleteClanResponse.isLoading}
                                    variant="outlined" color="error"
                                    startIcon={<DeleteIcon/>}
                                    disabled={clanIdEmpty}
                                    onClick={() => openConfirm('Delete Clan',
                                        `DELETE clan ${clanId}? This cannot be undone!`,
                                        () => handleAction(deleteClan, {clanId}, 'Clan deleted'))}>
                                    Delete Clan
                                </LoadingButton>
                            </Stack>
                        </CardContent>

                        <Divider/>

                        <Table sx={{mx: 2, mb: 2, width: 'auto'}}>
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{width: 160, py: 1.5, pl: 0, borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'middle'}}>
                                        <Typography variant="h6">Rename Clan</Typography>
                                    </TableCell>
                                    <TableCell sx={{width: 240, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'middle'}}>
                                        <TextField fullWidth label="New Name" size="small" value={newName}
                                                   onChange={(e) => setNewName(e.target.value)}/>
                                    </TableCell>
                                    <TableCell sx={{py: 1.5, pr: 0, borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'middle'}}>
                                        <LoadingButton loading={renameClanResponse.isLoading}
                                                       variant="outlined" color="info" startIcon={<EditIcon/>}
                                                       disabled={clanIdEmpty || !newName.trim()}
                                                       onClick={() => openConfirm('Rename Clan',
                                                           `Rename clan ${clanId} to "${newName}"?`,
                                                           () => handleAction(renameClan, {clanId, name: newName}, 'Clan renamed'))}>
                                            Rename
                                        </LoadingButton>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{width: 160, py: 1.5, pl: 0, borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'middle'}}>
                                        <Typography variant="h6">Add Experience</Typography>
                                    </TableCell>
                                    <TableCell sx={{width: 240, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'middle'}}>
                                        <TextField fullWidth label="Experience" size="small" type="number" value={experience}
                                                   onChange={(e) => setExperience(e.target.value)}/>
                                    </TableCell>
                                    <TableCell sx={{py: 1.5, pr: 0, borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'middle'}}>
                                        <LoadingButton loading={addExperienceResponse.isLoading}
                                                       variant="outlined" color="success" startIcon={<AddCircleIcon/>}
                                                       disabled={clanIdEmpty || !experience}
                                                       onClick={() => handleAction(addExperience, {clanId, experience: parseInt(experience)}, 'Experience added')}>
                                            Add Experience
                                        </LoadingButton>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{width: 160, py: 1.5, pl: 0, border: 0, verticalAlign: 'middle'}}>
                                        <Typography variant="h6">Add Reputation</Typography>
                                    </TableCell>
                                    <TableCell sx={{width: 240, py: 1.5, border: 0, verticalAlign: 'middle'}}>
                                        <TextField fullWidth label="Score" size="small" type="number" value={repScore}
                                                   onChange={(e) => setRepScore(e.target.value)}/>
                                    </TableCell>
                                    <TableCell sx={{py: 1.5, pr: 0, border: 0, verticalAlign: 'middle'}}>
                                        <LoadingButton loading={addReputationResponse.isLoading}
                                                       variant="outlined" color="success" startIcon={<AddCircleIcon/>}
                                                       disabled={!repScore || deviceIdIsEmpty}
                                                       onClick={() => handleAction(addReputation, {userId: deviceId, score: parseInt(repScore)}, 'Reputation added')}>
                                            Add Reputation
                                        </LoadingButton>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Card>
                </Grid>
                <Grid size={4}>
                    <TohBanStatusCard userId={deviceId}/>

                </Grid>


            </Grid>
            <Dialog open={confirmDialog.open} onClose={closeConfirm}>
                <DialogContent>
                    <Typography variant="h6">{confirmDialog.title}</Typography>
                    <Typography sx={{mt: 1}}>{confirmDialog.message}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConfirm}>Cancel</Button>
                    <LoadingButton variant="outlined" color="warning" onClick={confirmDialog.onConfirm}>
                        Confirm
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
}


const MESSAGE_TYPE_COLORS = {
    TEXT: 'default',
    JOIN_STATUS: 'info',
    MEMBER_STATUS: 'warning',
    CLAN_RENAME: 'info',
    ROLE_CHANGE: 'secondary',
    HELP_REQUEST: 'success',
    SHARE_CLAN: 'primary',
    LEVEL_UPDATE: 'success',
    DEPOT_LEADERBOARD_QUALIFIED: 'warning',
    DEPOT_CARRIAGE_MILESTONE: 'warning',
    DEPOT_TOP_100_ENTRY: 'warning',
};

function ChatMessage({msg, onUserClick}) {
    const isSystem = msg.type !== 'TEXT';
    const createdAt = msg.createdAt
        ? format(new Date(msg.createdAt), "dd.MM.yyyy HH:mm:ss")
        : '';

    const customText = msg.type === 'TEXT'
        ? msg.customData?.text || ''
        : msg.type === 'SHARE_CLAN'
            ? `Shared clan: ${msg.customData?.clan?.name || ''}`
            : JSON.stringify(msg.customData || {});

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 1.5, mb: 1,
                bgcolor: msg.deleted ? 'action.disabledBackground' : (isSystem ? 'grey.50' : 'background.paper'),
                opacity: msg.deleted ? 0.6 : 1,
            }}
        >
            <Stack
                direction="row"
                sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5
                }}>
                <Stack direction="row" spacing={1} sx={{
                    alignItems: "center"
                }}>
                    <Typography
                        variant="subtitle2"
                        sx={{cursor: 'pointer', '&:hover': {textDecoration: 'underline'}, color: 'primary.main'}}
                        onClick={() => onUserClick(msg.userId)}
                    >
                        {msg.userId}
                    </Typography>
                    <Link to={`/toh/dashboard/${msg.userId}`} style={{textDecoration: 'none'}}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: "info.main",
                                '&:hover': {textDecoration: 'underline'}
                            }}>
                            to state
                        </Typography>
                    </Link>
                    <Chip
                        label={msg.type}
                        size="small"
                        color={MESSAGE_TYPE_COLORS[msg.type] || 'default'}
                        variant="outlined"
                    />
                    {msg.deleted && <Chip label="deleted" size="small" color="error" variant="outlined"/>}
                </Stack>
                <Typography variant="caption" sx={{
                    color: "text.secondary"
                }}>{createdAt}</Typography>
            </Stack>
            <Typography variant="body2" sx={{wordBreak: 'break-word', whiteSpace: 'pre-wrap'}}>
                {customText}
            </Typography>
        </Paper>
    );
}


function ChatTab() {
    const useSnackBars = () => useContext(SnackBarContext);
    const {addAlert} = useSnackBars();

    const [getChat, getChatResponse] = useGetGlobalChatTOHMutation();
    const [mutePlayer, muteResponse] = useMutePlayerTOHMutation();
    const [unmutePlayer, unmuteResponse] = useUnmutePlayerTOHMutation();

    const [since, setSince] = useState('');
    const [reason, setReason] = useState('');
    const [limit, setLimit] = useState('');
    const [muteUserId, setMuteUserId] = useState('');

    const defaultMuteUntil = useMemo(() => {
        const d = new Date(Date.now() + 24 * 60 * 60);
        return d.toISOString().slice(0, 16);
    }, []);
    const [muteUntil, setMuteUntil] = useState(defaultMuteUntil);

    const messages = getChatResponse.data?.messages || [];

    useEffect(() => {
        getChat({since: 0, limit: 0});
    }, []);

    const handleLoadChat = async () => {
        const sinceValue = since ? new Date(since).getTime() : 0;
        const limitValue = limit ? parseInt(limit) : 0;
        const response = await getChat({since: sinceValue, limit: limitValue});
        if (response.hasOwnProperty('error')) {
            addAlert({text: response.error?.data?.message || 'Error loading chat', type: 'error'});
        }
    };

    const handleMute = async () => {
        const epochSeconds = Math.floor(new Date(muteUntil).getTime())/1000;

        const response = await mutePlayer({
            userId: muteUserId,
            mutedUntilSeconds: epochSeconds,
            reason: reason
        });
        setReason('')
        if (response.hasOwnProperty('error')) {
            addAlert({text: response.error?.data?.message || 'Error', type: 'error'});
        } else {
            addAlert({text: `Player ${muteUserId} muted`, type: 'success'});
        }
    };

    const handleUnmute = async () => {
        const response = await unmutePlayer({userId: muteUserId});
        if (response.hasOwnProperty('error')) {
            addAlert({text: response.error?.data?.message || 'Error', type: 'error'});
        } else {
            addAlert({text: `Player ${muteUserId} unmuted`, type: 'success'});
        }
    };

    return (
        <Grid container spacing={2}>
            {/* Chat messages */}
            <Grid size={9}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Global Chat</Typography>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                alignItems: "center",
                                mb: 2
                            }}>
                            <TextField
                                label="Since" size="small" type="datetime-local"
                                InputLabelProps={{shrink: true}} value={since}
                                onChange={(e) => setSince(e.target.value)}
                            />
                            <TextField
                                label="Limit" size="small" type="number" sx={{width: 120}} value={limit}
                                onChange={(e) => setLimit(e.target.value)}
                                placeholder="0"
                            />
                            <LoadingButton
                                loading={getChatResponse.isLoading}
                                variant="outlined" color="info"
                                startIcon={<SearchIcon/>}
                                onClick={handleLoadChat}>
                                Load
                            </LoadingButton>
                        </Stack>

                        <Box sx={{maxHeight: 600, overflowY: 'auto', pr: 1}}>
                            {messages.length > 0 ? (
                                messages.map((msg) => (
                                    <ChatMessage
                                        key={msg.id}
                                        msg={msg}
                                        onUserClick={(userId) => setMuteUserId(userId)}
                                    />
                                ))
                            ) : (
                                <Typography sx={{
                                    color: "text.secondary"
                                }}>
                                    {getChatResponse.isUninitialized
                                        ? 'Click "Load" to fetch chat messages'
                                        : 'No messages'}
                                </Typography>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
            {/* Mute / Unmute */}
            <Grid size={3}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Mute / Unmute</Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: "text.secondary",
                                display: 'block',
                                mb: 1
                            }}>
                            Click on a user ID in chat to fill the field
                        </Typography>
                        <TextField
                            fullWidth label="User ID" size="small" sx={{mb: 2}} value={muteUserId}
                            onChange={(e) => setMuteUserId(e.target.value)}
                        />
                        <TextField
                            fullWidth label="Mute until" size="small" type="datetime-local" sx={{mb: 2}}
                            InputLabelProps={{shrink: true}}
                            value={muteUntil}
                            onChange={(e) => setMuteUntil(e.target.value)}
                        />

                        <TextField
                            fullWidth label="Reason" size="small" sx={{mb: 2}}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />

                        <Stack direction="column" spacing={1}>
                            <LoadingButton
                                loading={muteResponse.isLoading}
                                variant="outlined" color="warning"
                                startIcon={<VolumeOffIcon/>}
                                disabled={!muteUserId.trim() || !muteUntil}
                                onClick={handleMute}
                                fullWidth>
                                Mute
                            </LoadingButton>
                            <LoadingButton
                                loading={unmuteResponse.isLoading}
                                variant="outlined" color="success"
                                startIcon={<VolumeUpIcon/>}
                                disabled={!muteUserId.trim()}
                                onClick={handleUnmute}
                                fullWidth>
                                Unmute
                            </LoadingButton>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
}


const DEFAULT_MESSAGE_TYPE = 'TEXT';
const ALL_MESSAGE_TYPES = '__ALL__';
// The backend returns the newest N of the range, so a small limit silently hides the start
// of the period. Its own default is 100, which is not enough for a busy clan even for one day.
const DEFAULT_CHAT_LIMIT = 2000;

function ClanChatTab({defaultClanId}) {
    const useSnackBars = () => useContext(SnackBarContext);
    const {addAlert} = useSnackBars();

    const [getChat, getChatResponse] = useGetClanChatTOHMutation();

    const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
    const [clanId, setClanId] = useState(defaultClanId || '');
    const [clanIdTouched, setClanIdTouched] = useState(false);
    const [dateFrom, setDateFrom] = useState(today);
    const [dateTo, setDateTo] = useState(today);
    const [limit, setLimit] = useState('');
    const [autoLoaded, setAutoLoaded] = useState(false);
    const [userIdFilter, setUserIdFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState(DEFAULT_MESSAGE_TYPE);
    const [loadedLimit, setLoadedLimit] = useState(DEFAULT_CHAT_LIMIT);

    // Player's clan id arrives async; keep it as default until edited manually.
    useEffect(() => {
        if (!clanIdTouched && defaultClanId) setClanId(defaultClanId);
    }, [defaultClanId, clanIdTouched]);

    // yyyy-MM-dd strings compare chronologically, so plain string comparison is enough here.
    // The native min/max below cover the picker; these guards cover typed-in dates.
    const handleDateFromChange = (value) => {
        setDateFrom(value);
        if (value && dateTo && value > dateTo) setDateTo(value);
    };

    const handleDateToChange = (value) => {
        setDateTo(value && dateFrom && value < dateFrom ? dateFrom : value);
    };

    // Memoised so the filters below do not recompute on every render (`|| []` is a fresh array).
    const messages = useMemo(() => getChatResponse.data?.messages || [], [getChatResponse.data]);
    const clanIdEmpty = !clanId || clanId.trim() === '';

    // Types offered come from what was actually loaded; TEXT and the current pick are always
    // listed so the default stays selectable on a page that happens to have none of them.
    const typeOptions = useMemo(() => {
        const types = new Set(messages.map((m) => m.type).filter(Boolean));
        types.add(DEFAULT_MESSAGE_TYPE);
        types.add(typeFilter);
        return Array.from(types).filter((t) => t !== ALL_MESSAGE_TYPES).sort();
    }, [messages, typeFilter]);

    const visibleMessages = useMemo(() => {
        const needle = userIdFilter.trim().toLowerCase();
        return messages.filter((msg) => {
            if (typeFilter !== ALL_MESSAGE_TYPES && msg.type !== typeFilter) return false;
            return needle === '' || (msg.userId || '').toLowerCase().includes(needle);
        });
    }, [messages, userIdFilter, typeFilter]);

    const loadChat = async (id) => {
        const since = new Date(dateFrom + 'T00:00:00').getTime();
        const till = new Date(dateTo + 'T00:00:00').getTime() + 24 * 60 * 60 * 1000;
        const requestedLimit = limit ? parseInt(limit) : DEFAULT_CHAT_LIMIT;
        setLoadedLimit(requestedLimit);
        const response = await getChat({
            clanId: id.trim(),
            since,
            till,
            limit: requestedLimit,
        });
        if (response.hasOwnProperty('error')) {
            addAlert({text: response.error?.data?.message || 'Error loading clan chat', type: 'error'});
        }
    };

    // Default view: today's messages of the player's clan, once its id is known.
    useEffect(() => {
        if (!autoLoaded && !clanIdEmpty) {
            setAutoLoaded(true);
            loadChat(clanId);
        }
    }, [clanId, autoLoaded, clanIdEmpty]);

    return (
        <Grid container spacing={2}>
            <Grid size={9}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Clan Chat</Typography>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                alignItems: "center",
                                mb: 2
                            }}>
                            <TextField
                                label="Clan ID (UUID)" size="small" sx={{width: 320}} value={clanId}
                                onChange={(e) => {
                                    setClanId(e.target.value);
                                    setClanIdTouched(true);
                                }}
                            />
                            <TextField
                                label="From" size="small" type="date"
                                InputLabelProps={{shrink: true}} value={dateFrom}
                                onChange={(e) => handleDateFromChange(e.target.value)}
                            />
                            <TextField
                                label="To" size="small" type="date"
                                InputLabelProps={{shrink: true}} value={dateTo}
                                slotProps={{htmlInput: {min: dateFrom}}}
                                onChange={(e) => handleDateToChange(e.target.value)}
                            />
                            <TextField
                                label="Limit" size="small" type="number" sx={{width: 120}} value={limit}
                                onChange={(e) => setLimit(e.target.value)}
                                placeholder={String(DEFAULT_CHAT_LIMIT)}
                            />
                            <LoadingButton
                                loading={getChatResponse.isLoading}
                                variant="outlined" color="info"
                                startIcon={<SearchIcon/>}
                                disabled={clanIdEmpty || !dateFrom || !dateTo}
                                onClick={() => loadChat(clanId)}>
                                Load
                            </LoadingButton>
                        </Stack>

                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                alignItems: "center",
                                mb: 2
                            }}>
                            <TextField
                                label="User ID" size="small" sx={{width: 320}} value={userIdFilter}
                                onChange={(e) => setUserIdFilter(e.target.value)}
                                placeholder="filter loaded messages"
                            />
                            <FormControl size="small" sx={{minWidth: 200}}>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    label="Type"
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                >
                                    <MenuItem value={ALL_MESSAGE_TYPES}>All types</MenuItem>
                                    {typeOptions.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Typography variant="caption" sx={{color: "text.secondary"}}>
                                {visibleMessages.length} of {messages.length} loaded
                            </Typography>
                            {messages.length >= loadedLimit && (
                                <Typography variant="caption" sx={{color: "warning.main"}}>
                                    limit reached - only the {loadedLimit} newest messages of the period are shown,
                                    narrow the dates or raise Limit
                                </Typography>
                            )}
                        </Stack>

                        <Box sx={{maxHeight: 600, overflowY: 'auto', pr: 1}}>
                            {visibleMessages.length > 0 ? (
                                visibleMessages.map((msg) => (
                                    <ChatMessage
                                        key={msg.id}
                                        msg={msg}
                                        onUserClick={(userId) => setUserIdFilter(userId)}
                                    />
                                ))
                            ) : (
                                <Typography sx={{
                                    color: "text.secondary"
                                }}>
                                    {getChatResponse.isUninitialized
                                        ? (clanIdEmpty
                                            ? 'Enter a Clan ID (or open a player with a clan) and click "Load"'
                                            : 'Click "Load" to fetch chat messages')
                                        : messages.length > 0
                                            ? 'No messages match the filters'
                                            : 'No messages for the selected period'}
                                </Typography>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
}


export default ClansTOH
